import { CSSProperties, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { AutocompleteDropdown, AutocompleteDropdownItem, Input } from "../../../../shared/components/FormType/Styled";
import { color } from "../../../../shared/styles";
import api from "../../../../utils/api";

const Picker = styled.div`
    width: 100%;
    min-width: 0;
    flex: 1;
`;
const Popup = styled(AutocompleteDropdown)`
    box-sizing: border-box;
    margin-top: 0;
    font-size: 13px;
    line-height: 20px;
`;
const Option = styled(AutocompleteDropdownItem)<{ $active: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    background: ${({ $active }) => ($active ? color.emphasis.NEUTRAL_N5 : "transparent")};
    cursor: pointer;
    span:first-child {
        min-width: 0;
        overflow-wrap: break-word;
    }
    small {
        flex-shrink: 0;
        color: ${color.emphasis.NEUTRAL_N70};
        font-size: 12px;
    }
`;
const DropdownMessage = styled.div`
    padding: 10px;
    font-family: "Noto Sans", sans-serif;
    font-size: 13px;
    line-height: 20px;
`;
const DropdownAction = styled.button`
    width: 100%;
    padding: 10px;
    border: 0;
    background: ${color.emphasis.NEUTRAL_N5};
    color: ${color.primary.BLUE_B100};
    font: inherit;
    cursor: pointer;
    &:disabled {
        cursor: wait;
        opacity: 0.6;
    }
`;

export type Recipient = { key: string; name: string; kind: "Khách hàng" | "Nhà cung cấp" };
export async function searchRecipients(search: string, page: number, enabled = [true, true]) {
    const urls = ["/customers/", "/materials/providers/"];
    const responses: any[] = await Promise.all(
        urls.map((url, index) =>
            enabled[index]
                ? api.get(`${url}?search=${encodeURIComponent(search)}&page=${page}&page_size=50`)
                : Promise.resolve({ results: [], next: null }),
        ),
    );

    return {
        options: responses.flatMap((data, index) =>
            (data.results || data).map((item: any) => ({
                key: `${index}:${item.id}`,
                name: item.name,
                kind: index === 0 ? "Khách hàng" : "Nhà cung cấp",
            })),
        ) as Recipient[],
        hasMore: responses.some((data) => Boolean(data.next)),
        nextSources: responses.map((data) => Boolean(data.next)),
    };
}

export default function RecipientPicker({
    value = "",
    onSelect,
}: {
    value?: string;
    onSelect: (name: string) => void;
}) {
    const listId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const lastNotified = useRef(value);
    const nextSources = useRef([true, true]);
    const [input, setInput] = useState(value);
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<Recipient[]>([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [retry, setRetry] = useState(0);
    const [position, setPosition] = useState<CSSProperties>({});

    useEffect(() => {
        if (value !== lastNotified.current) {
            setInput(value);
            setQuery("");
            setPage(1);
            lastNotified.current = value;
        }
    }, [value]);

    useLayoutEffect(() => {
        if (!open) return;
        const place = () => {
            const rect = inputRef.current?.getBoundingClientRect();
            if (!rect) return;
            const below = window.innerHeight - rect.bottom - 8;
            const above = rect.top - 8;
            const upwards = below < 180 && above > below;
            setPosition({
                position: "fixed",
                left: rect.left,
                width: rect.width,
                right: "auto",
                top: upwards ? "auto" : rect.bottom + 4,
                bottom: upwards ? window.innerHeight - rect.top + 4 : "auto",
                maxHeight: Math.max(80, Math.min(240, upwards ? above : below)),
                zIndex: 1400,
            });
        };
        const dismiss = (event: MouseEvent) => {
            const target = event.target as Node;
            if (!inputRef.current?.contains(target) && !popupRef.current?.contains(target)) setOpen(false);
        };
        place();
        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, true);
        document.addEventListener("mousedown", dismiss);
        return () => {
            window.removeEventListener("resize", place);
            window.removeEventListener("scroll", place, true);
            document.removeEventListener("mousedown", dismiss);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        let active = true;
        setLoading(true);
        setFailed(false);
        const timer = setTimeout(async () => {
            try {
                const result = await searchRecipients(query, page, page === 1 ? [true, true] : nextSources.current);
                if (active) {
                    setOptions((previous) =>
                        Array.from(
                            new Map(
                                (page === 1 ? result.options : [...previous, ...result.options]).map((o) => [o.key, o]),
                            ).values(),
                        ),
                    );
                    setHasMore(result.hasMore);
                    nextSources.current = result.nextSources;
                }
            } catch {
                if (active) {
                    setFailed(true);
                    setHasMore(false);
                    setOptions([]);
                }
            } finally {
                if (active) setLoading(false);
            }
        }, 250);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [query, page, open, retry]);

    const select = (recipient: Recipient) => {
        setInput(recipient.name);
        lastNotified.current = recipient.name;
        onSelect(recipient.name);
        setOpen(false);
        setActiveIndex(-1);
    };
    const startSearch = (text: string) => {
        setInput(text);
        setQuery(text);
        setPage(1);
        setOptions([]);
        setHasMore(false);
        setActiveIndex(-1);
        setOpen(true);
        lastNotified.current = "";
        onSelect("");
    };
    return (
        <Picker>
            <Input
                ref={inputRef}
                role="combobox"
                aria-label="Đối tượng"
                aria-required="true"
                aria-expanded={open}
                aria-controls={open ? listId : undefined}
                aria-autocomplete="list"
                aria-activedescendant={open && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
                placeholder="Tìm kiếm..."
                autoComplete="off"
                value={input}
                onFocus={() => setOpen(true)}
                onMouseDown={() => setOpen(true)}
                onChange={(event) => startSearch(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === "Escape" || event.key === "Tab") {
                        setOpen(false);
                        return;
                    }
                    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                        event.preventDefault();
                        setOpen(true);
                        const next = Math.max(
                            0,
                            Math.min(options.length - 1, activeIndex + (event.key === "ArrowDown" ? 1 : -1)),
                        );
                        setActiveIndex(next);
                        document.getElementById(`${listId}-${next}`)?.scrollIntoView?.({ block: "nearest" });
                    }
                    if (event.key === "Enter" && open && options[activeIndex]) {
                        event.preventDefault();
                        select(options[activeIndex]);
                    }
                }}
            />
            {open &&
                createPortal(
                    <Popup ref={popupRef} style={position}>
                        <div role="listbox" id={listId} aria-label="Danh sách đối tượng" aria-busy={loading}>
                            {options.map((recipient, index) => (
                                <Option
                                    role="option"
                                    key={recipient.key}
                                    id={`${listId}-${index}`}
                                    aria-label={`${recipient.name} — ${recipient.kind}`}
                                    aria-selected={activeIndex === index}
                                    $active={activeIndex === index}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => select(recipient)}
                                >
                                    <span>{recipient.name}</span>
                                    <small>{recipient.kind}</small>
                                </Option>
                            ))}
                        </div>
                        {loading && <DropdownMessage role="status">Đang tìm đối tượng…</DropdownMessage>}
                        {failed && (
                            <DropdownMessage role="alert">
                                Không tải được danh sách đối tượng.
                                <DropdownAction
                                    type="button"
                                    onClick={() => {
                                        setPage(1);
                                        setRetry((n) => n + 1);
                                    }}
                                >
                                    Thử lại
                                </DropdownAction>
                            </DropdownMessage>
                        )}
                        {!loading && !failed && !options.length && (
                            <DropdownMessage>Không tìm thấy đối tượng</DropdownMessage>
                        )}
                        {hasMore && !failed && (
                            <DropdownAction type="button" disabled={loading} onClick={() => setPage((p) => p + 1)}>
                                Tải thêm đối tượng
                            </DropdownAction>
                        )}
                    </Popup>,
                    document.body,
                )}
        </Picker>
    );
}
